import React, { useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    LinearProgress,
    Box,
    IconButton,
    makeStyles,
} from '@material-ui/core';
import { Close, CloudUpload, Description } from '@material-ui/icons';
import { convertPdfToThumbnails } from './pdfConverter';
import { initializePdfUpload, uploadPdfFile, uploadThumbnail, clearPdfBoardCache, getPdfBoardId } from './uploadService';
import collectionsApi from '../../../Api/collectionsApi';

const MAX_FILE_SIZE = 6 * 1024 * 1024;

const useStyles = makeStyles((theme) => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    uploadArea: {
        border: `2px dashed ${theme.palette.primary.main}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(4),
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    fileInput: {
        display: 'none',
    },
    progressContainer: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    statusText: {
        marginTop: theme.spacing(1),
        color: theme.palette.text.secondary,
    },
    icon: {
        fontSize: 48,
        color: theme.palette.primary.main,
        marginBottom: theme.spacing(2),
    },
    errorText: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(1),
    },
}));

const PDFProcessDialog = ({ open, onClose, collectionId, intl }) => {
    const classes = useStyles();
    const [selectedFile, setSelectedFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [statusMessage, setStatusMessage] = useState('');
    const [uploadedImages, setUploadedImages] = useState([]);
    const [fileError, setFileError] = useState('');
    const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

    const fileInputRef = React.useRef(null);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        setFileError('');
        
        if (file && file.type === 'application/pdf') {
            if (file.size > MAX_FILE_SIZE) {
                setFileError(intl.formatMessage(
                    { id: 'pdf.process.fileTooLarge' },
                    { maxSize: '6 MB', actualSize: (file.size / 1024 / 1024).toFixed(2) + ' MB' }
                ));
                setSelectedFile(null);
                setStatusMessage('');
            } else {
                setSelectedFile(file);
                setStatusMessage(intl.formatMessage({ id: 'pdf.process.fileSelected' }, { name: file.name }));
            }
        } else {
            setStatusMessage(intl.formatMessage({ id: 'pdf.process.invalidFile' }));
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleProcess = async () => {
        if (!selectedFile) return;

        setIsProcessing(true);
        setProgress(0);
        setUploadedImages([]);
        setStatusMessage(intl.formatMessage({ id: 'pdf.process.converting' }));

        try {
            const { thumbnails, pageCount } = await convertPdfToThumbnails(selectedFile, (pageNum, total) => {
                setCurrentPage(pageNum);
                setTotalPages(total);
                setProgress((pageNum / total) * 20);
                setStatusMessage(intl.formatMessage({ id: 'pdf.process.convertingPage' }, { current: pageNum, total }));
            });

            setProgress(20);
            setStatusMessage(intl.formatMessage({ id: 'pdf.process.initializing' }));

            await initializePdfUpload(selectedFile.name, pageCount, collectionId);

            setProgress(30);
            setStatusMessage('Uploading PDF file...');
            const pdfResult = await uploadPdfFile(selectedFile);

            setStatusMessage('Uploading thumbnails...');
            const uploadedThumbnails = [];

            for (let i = 0; i < thumbnails.length; i++) {
                const thumbnail = thumbnails[i];
                setCurrentPage(thumbnail.pageNumber);
                setProgress(30 + ((thumbnail.pageNumber / thumbnails.length) * 60));
                setStatusMessage(`Uploading thumbnail ${thumbnail.pageNumber} of ${thumbnails.length}...`);

                const thumbnailData = await uploadThumbnail(thumbnail.thumbnail, thumbnail.pageNumber);
                uploadedThumbnails.push(thumbnailData);
            }

            setUploadedImages(uploadedThumbnails.map(t => t.thumbnailUrl));
            
            const boardId = getPdfBoardId();
            if (boardId) {
                const updatedMeta = {
                    pdfFileName: selectedFile.name,
                    pageCount: pageCount,
                    pdfUrl: pdfResult.pdfUrl,
                    thumbnails: uploadedThumbnails.map(t => ({ pageNumber: t.pageNumber })),
                };
                await collectionsApi.updateBoard(boardId, { 
                    name: selectedFile.name,
                    meta: JSON.stringify(updatedMeta)
                });
            }
            
            setProgress(100);
            setStatusMessage(intl.formatMessage({ id: 'pdf.process.success' }, { count: pageCount }));
        } catch (error) {
            console.error('Error processing PDF:', error);
            setStatusMessage(intl.formatMessage({ id: 'pdf.process.error' }, { message: error.message }));
        } finally {
            clearPdfBoardCache();
            setIsProcessing(false);
        }
    };

    const handleClose = () => {
        if (isProcessing) {
            setShowCloseConfirmation(true);
        } else {
            setSelectedFile(null);
            setProgress(0);
            setCurrentPage(0);
            setTotalPages(0);
            setStatusMessage('');
            setUploadedImages([]);
            setFileError('');
            onClose();
        }
    };

    const handleConfirmClose = async () => {
        setShowCloseConfirmation(false);
        
        const boardId = getPdfBoardId();
        if (boardId) {
            try {
                await collectionsApi.deleteBoard(boardId);
            } catch (error) {
                console.error('Error deleting interrupted upload:', error);
            }
        }
        
        clearPdfBoardCache();
        setSelectedFile(null);
        setProgress(0);
        setCurrentPage(0);
        setTotalPages(0);
        setStatusMessage('');
        setUploadedImages([]);
        setFileError('');
        setIsProcessing(false);
        onClose();
    };

    const handleCancelClose = () => {
        setShowCloseConfirmation(false);
    };

    return (
        <>
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle disableTypography>
                <Typography variant="h4">
                    <FormattedMessage id="pdf.process.title" />
                </Typography>
                <IconButton
                    aria-label="close"
                    className={classes.closeButton}
                    onClick={handleClose}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                {!selectedFile && !isProcessing && (
                    <Box className={classes.uploadArea} onClick={handleUploadClick}>
                        <Description className={classes.icon} />
                        <Typography variant="h6">
                            <FormattedMessage id="pdf.process.selectFile" />
                        </Typography>
                        <Typography variant="body2" className={classes.statusText}>
                            <FormattedMessage id="pdf.process.supportedFormat" />
                        </Typography>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf"
                            onChange={handleFileSelect}
                            className={classes.fileInput}
                        />
                    </Box>
                )}

                {selectedFile && !isProcessing && (
                    <Box>
                        <Typography variant="body1" gutterBottom>
                            <strong>{intl.formatMessage({ id: 'pdf.process.selectedFile' })}</strong> {selectedFile.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {intl.formatMessage(
                                { id: 'pdf.process.fileSize' },
                                { size: (selectedFile.size / 1024 / 1024).toFixed(2) + ' MB' }
                            )}
                        </Typography>
                    </Box>
                )}

                {fileError && (
                    <Box>
                        <Typography variant="body1" className={classes.errorText}>
                            {fileError}
                        </Typography>
                    </Box>
                )}

                {isProcessing && (
                    <Box className={classes.progressContainer}>
                        <LinearProgress variant="determinate" value={progress} />
                        <Typography variant="body2" className={classes.statusText}>
                            {statusMessage}
                        </Typography>
                        {totalPages > 0 && (
                            <Typography variant="caption" color="textSecondary">
                                {intl.formatMessage(
                                    { id: 'pdf.process.pageProgress' },
                                    { current: currentPage, total: totalPages }
                                )}
                            </Typography>
                        )}
                    </Box>
                )}

                {!isProcessing && uploadedImages.length > 0 && (
                    <Box className={classes.progressContainer}>
                        <Typography variant="body1" color="primary" gutterBottom>
                            {statusMessage}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {intl.formatMessage(
                                { id: 'pdf.process.imagesUploaded' },
                                { count: uploadedImages.length }
                            )}
                        </Typography>
                    </Box>
                )}

                {statusMessage && !isProcessing && uploadedImages.length === 0 && selectedFile && (
                    <Typography variant="body2" className={classes.statusText}>
                        {statusMessage}
                    </Typography>
                )}
            </DialogContent>

            <DialogActions style={{ marginBottom: '0.5rem', marginRight: '1rem' }}>
                {!isProcessing && !uploadedImages.length && (
                    <>
                        <Button onClick={handleClose} variant="contained" color="default">
                            <FormattedMessage id="close" />
                        </Button>
                        <Button
                            onClick={handleProcess}
                            variant="contained"
                            color="primary"
                            disabled={!selectedFile}
                            startIcon={<CloudUpload />}
                        >
                            <FormattedMessage id="pdf.process.convert" />
                        </Button>
                    </>
                )}
                {!isProcessing && uploadedImages.length > 0 && (
                    <Button onClick={handleClose} variant="contained" color="primary">
                        <FormattedMessage id="close" />
                    </Button>
                )}
            </DialogActions>
        </Dialog>
        
        <Dialog open={showCloseConfirmation} onClose={handleCancelClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                <FormattedMessage id="pdf.process.confirmClose.title" />
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1">
                    <FormattedMessage id="pdf.process.confirmClose.message" />
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancelClose} variant="contained" color="default">
                    <FormattedMessage id="pdf.process.confirmClose.cancel" />
                </Button>
                <Button onClick={handleConfirmClose} variant="contained" color="secondary">
                    <FormattedMessage id="pdf.process.confirmClose.confirm" />
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default injectIntl(PDFProcessDialog);
